import { useQuery } from '@apollo/client';
import { JOIN_NOW_FORM_QUERY } from '../operations';

// Global text cache to maintain existing behavior
let textCache = null;

/**
 * Custom hook for managing JoinForm GraphQL data fetching and caching
 *
 * Handles the JOIN_NOW_FORM_QUERY and structures the response data for easy consumption
 * by the JoinForm component. Maintains backward compatibility with global text cache.
 *
 * @returns {Object} Hook interface containing query state and structured data
 * @returns {boolean} returns.isLoading - GraphQL query loading state
 * @returns {Error|null} returns.error - GraphQL query error if any
 * @returns {Object} returns.data - Raw GraphQL response data
 * @returns {Object} returns.textFor - Localized text content for all form fields
 * @returns {Object} returns.config - Form configuration settings and feature flags
 * @returns {Object} returns.joinForm - Specific join form configuration and structure
 * @returns {string} returns.userCountry - User's detected country code
 * @returns {string} returns.signifydId - Device fingerprinting identifier
 * @returns {boolean} returns.isPasswordStrengthEnabled - Password strength validation flag
 * @returns {string} returns.formId - Form identifier for HTML form element
 * @returns {Object} returns.joinFormLabel - Form title and labeling information
 * @returns {Array} returns.formGroupList - Structured form field groups for rendering
 */
export const useJoinFormData = () => {
  const { data = {}, loading: queryLoading, error: queryError } = useQuery(JOIN_NOW_FORM_QUERY);
  const { textFor = {}, config = {} } = data;
  const { joinForm = {} } = textFor;

  textCache = textFor;

  const { userCountry, signifydId } = data;

  return {
    isLoading: queryLoading,
    error: queryError,
    data,
    textFor,
    config,
    joinForm,
    userCountry,
    signifydId,
    formId: joinForm.id || 'join-form',
    joinFormLabel: joinForm.label,
    formGroupList: joinForm.formGroupList,
  };
};

export const getTextCache = () => textCache;
